// ============================================================
// RmkHand — 루미큐브 Konva 손패 Layer  (SEQ-10214 S5c)
//   TD §3.2 HandLayer 1:1: 하단 25% 영역, 가로 스크롤, draggable
//   정보 비대칭 강제:
//     - 본인 손패만 face(color/number) 노출
//     - 타인 손패는 handSizes로 장수만 표시 (face 절대 금지)
//   TtrBoard.tsx Konva 패턴 정합
// ============================================================

import React, { useState, useCallback } from 'react'
import {
  Layer,
  Group,
  Rect,
  Text,
} from 'react-konva'
import type Konva from 'konva'
import { RmkTile, RmkPlayer } from '../store/rummikub'

// ── 타일 색 hex ─────────────────────────────────────────
const TILE_COLOR_HEX: Record<string, string> = {
  BLACK:  '#222222',
  RED:    '#D32F2F',
  BLUE:   '#1976D2',
  ORANGE: '#F57C00',
  JOKER:  '#7B1FA2',
}

// ── 타일 크기 상수 ─────────────────────────────────────────
const TILE_W    = 44
const TILE_H    = 52
const TILE_GAP  = 4
const HAND_PADDING_X = 12
const HAND_PADDING_Y = 8

// ── 정렬 모드 ────────────────────────────────────────────
type SortMode = 'none' | 'color' | 'number'

// ── Props ────────────────────────────────────────────────
export interface RmkHandProps {
  stageWidth:   number
  stageHeight:  number
  myHand:       RmkTile[]
  players:      RmkPlayer[]
  handSizes:    Map<number, number>  // 타인 장수 (face 없음)
  myUserCode:   number | null
  isMyTurn:     boolean
  // 드래그 시작 콜백 (PlayRummikub에서 dragTileId 추적)
  onDragStart?: (tileId: number) => void
  // 드래그 종료 (보드 드롭 — PlaceTile)
  onDropToBoard?: (tileId: number, targetSetId: number, position: number) => void
}

const RmkHand: React.FC<RmkHandProps> = ({
  stageWidth,
  stageHeight,
  myHand,
  players,
  handSizes,
  myUserCode,
  isMyTurn,
  onDragStart,
  onDropToBoard,
}) => {
  const [sortMode, setSortMode] = useState<SortMode>('none')

  // 손패 정렬
  const sortedHand = React.useMemo((): RmkTile[] => {
    const copy = [...myHand]
    if (sortMode === 'color') {
      copy.sort((a, b) =>
        a.color.localeCompare(b.color) || a.number - b.number,
      )
    } else if (sortMode === 'number') {
      copy.sort((a, b) =>
        a.number - b.number || a.color.localeCompare(b.color),
      )
    }
    return copy
  }, [myHand, sortMode])

  // SEQ=10221 손패 wrap fix: 한 줄에 들어가는 타일 수 계산 + 행 수에 맞춰 영역 높이 동적
  const handAreaW    = stageWidth
  const HAND_HEADER_H = 18  // "내 손패 N장" 라벨 영역
  const tilesPerRow  = Math.max(1, Math.floor((handAreaW - 2 * HAND_PADDING_X + TILE_GAP) / (TILE_W + TILE_GAP)))
  const rowCount     = Math.max(1, Math.ceil((myHand.length || 1) / tilesPerRow))
  const handBodyH    = HAND_PADDING_Y * 2 + HAND_HEADER_H + rowCount * (TILE_H + TILE_GAP)
  const minHandAreaH = stageHeight * 0.22
  const handAreaH    = Math.max(minHandAreaH, handBodyH)
  const handAreaY    = stageHeight - handAreaH

  const handleTileDragStart = useCallback(
    (_e: Konva.KonvaEventObject<DragEvent>, tileId: number) => {
      if (!isMyTurn) return
      onDragStart && onDragStart(tileId)
    },
    [isMyTurn, onDragStart],
  )

  const handleTileDragEnd = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>, tileId: number) => {
      if (!isMyTurn) {
        e.target.position({ x: 0, y: 0 })
        return
      }
      const stage = e.target.getStage()
      if (!stage) return
      const pos = stage.getPointerPosition()
      if (!pos) return

      // SEQ=10222 드롭 영역 fix: 손패 영역 위 32px margin 확대 + 진단 로그
      //   사용자 catch (SEQ=10221 wrap fix 부수효과로 handAreaY가 위로 올라가 드롭 인식 실패)
      const dropZoneY = handAreaY - 32
      const willDrop = pos.y < dropZoneY && !!onDropToBoard
      console.log('[RmkHand] dragEnd', { tileId, posY: pos.y, handAreaY, dropZoneY, willDrop })
      if (willDrop && onDropToBoard) {
        onDropToBoard(tileId, 0, 0)  // targetSetId=0=신규 set, position=0 (서버 자동 배치)
      }
      // 원위치 (서버 응답 HandStateUpdated로 확정)
      e.target.position({ x: 0, y: 0 })
    },
    [isMyTurn, handAreaY, onDropToBoard],
  )

  // 정렬 버튼 클릭 (Konva 외부 — HTML 버튼 사용, Konva Layer는 pure 렌더)
  void setSortMode

  return (
    <Layer>
      {/* 손패 배경 */}
      <Rect
        x={0}
        y={handAreaY}
        width={handAreaW}
        height={handAreaH}
        fill="#0a1a2a"
        stroke="#1976D2"
        strokeWidth={1}
      />

      {/* 본인 손패 카운터 */}
      <Text
        x={HAND_PADDING_X}
        y={handAreaY + HAND_PADDING_Y}
        text={`내 손패 ${myHand.length}장`}
        fontSize={13}
        fill="#90CAF9"
      />

      {/* 본인 손패 타일 (face 노출 — 본인만) — SEQ=10221: wrap 다행 지원 */}
      {sortedHand.map((tile, idx) => {
        const row = Math.floor(idx / tilesPerRow)
        const col = idx % tilesPerRow
        const tileX = HAND_PADDING_X + col * (TILE_W + TILE_GAP)
        const tileY = handAreaY + HAND_PADDING_Y + HAND_HEADER_H + row * (TILE_H + TILE_GAP)
        const bgColor = TILE_COLOR_HEX[tile.color] ?? '#555'
        const label = tile.isJoker ? '★' : String(tile.number)
        const textColor = tile.color === 'ORANGE' ? '#000' : '#fff'

        return (
          <Group
            key={tile.tileId}
            x={tileX}
            y={tileY}
            draggable={isMyTurn}
            onDragStart={(e: Konva.KonvaEventObject<DragEvent>) =>
              handleTileDragStart(e, tile.tileId)
            }
            onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) =>
              handleTileDragEnd(e, tile.tileId)
            }
          >
            <Rect
              x={0}
              y={0}
              width={TILE_W}
              height={TILE_H}
              fill={bgColor}
              cornerRadius={4}
              stroke={isMyTurn ? '#FFD700' : '#666'}
              strokeWidth={isMyTurn ? 1.5 : 1}
              shadowBlur={isMyTurn ? 6 : 0}
              shadowColor="rgba(255,215,0,0.5)"
            />
            <Text
              x={0}
              y={TILE_H / 2 - 10}
              width={TILE_W}
              text={label}
              fontSize={16}
              fontStyle="bold"
              fill={textColor}
              align="center"
            />
            {/* 조커 표시 */}
            {tile.isJoker && (
              <Text
                x={0}
                y={TILE_H - 14}
                width={TILE_W}
                text="J"
                fontSize={10}
                fill="#E040FB"
                align="center"
              />
            )}
          </Group>
        )
      })}

      {/* 타인 손패 장수 표시 (face 절대 금지 — handSizes만) */}
      {players
        .filter(p => p.userCode !== myUserCode)
        .map((p, idx) => {
          const sz = handSizes.get(p.userCode) ?? 0
          return (
            <Text
              key={p.userCode}
              x={HAND_PADDING_X + idx * 120}
              y={handAreaY + handAreaH - 20}
              text={`${p.nickname}: ${sz}장`}
              fontSize={11}
              fill="#aaa"
            />
          )
        })
      }
    </Layer>
  )
}

export default RmkHand
